/** @format */

import { Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton } from '@mui/material';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { Button, Toggle } from 'src/components';
import SortSelectDropdown from 'src/components/SortSelectDropdown';
import { CustomDropDown } from 'src/components/CustomDropDown/CustomDropDown';
import { TopicSelectModal } from 'src/components/personaldata';
import { TOPIC_SORT_OPTIONS } from 'src/constants';
import { getExtranetFilterLanguageItems } from 'src/utils/extranetLanguages';
import { getDefaultSearchLanguage } from 'src/slices/search';
import Breadcrumb from 'src/components/Breadcrumb';
import Page from 'src/components/Page';
import { useDispatch, useSelector } from 'src/store';
import useAuth from 'src/hooks/useAuth';
import {
  addUserTopicFavorite,
  removeUserTopicFavorite,
  getContentItems,
  getPublishedTopics,
  setPublishedTopicsLoading,
  setActiveTopicKey,
  setExpandedTopicKeys,
  clearExpandedTopicKeys,
  setActiveTopicBrand,
  setSelectedLanguage,
  setSelectedSortBy,
  toggleExpandedTopicKey,
  getActiveTopicDetail,
  setTopicDetail,
  clearContentItems
} from 'src/slices/topics';
import { ContentItem, ContentListSkeleton } from './contentItem';
import { CrossIcon, SettingIcon } from 'src/components/icons';
import { Link } from 'react-router-dom';
import type { Topic } from 'src/types';
import useTopicsSidebarSticky from 'src/hooks/useTopicsSidebarSticky';
import { resolveSelectedLanguage } from 'src/utils/locale';
import {
  buildExtranetLocation,
  collectFavoriteTopicsFlat,
  findTopicByIdInTree,
  findTopicPathInTree,
  getExpandedTopicKeysForActivePath,
  getTopicDisplayTitle,
  getTopicPathTitles,
  getTopicTreeNodeKey,
  isActiveExtranetTopic,
  hasChildren,
  isFavoriteViewSearch,
  resolveChildTopicsForReview,
  resolveExtranetContentHead,
  tryGetTopicId
} from './extranetTopicNav';

const topicTabs = [{ id: 'all' }, { id: 'ate' }, { id: 'vdo' }];

function syncChildTopicCardHeights(list: HTMLElement) {
  const items = Array.from(list.children).filter((node): node is HTMLElement => node instanceof HTMLElement);
  let maxHeight = 0;

  items.forEach(item => {
    const card = item.querySelector<HTMLElement>('.topics-page__children-topic');
    if (!card) {
      return;
    }
    const title = card.querySelector('h3');
    const count = card.querySelector('span');
    const contentHeight = Math.max(
      title?.getBoundingClientRect().height ?? 0,
      count?.getBoundingClientRect().height ?? 0
    );
    const style = window.getComputedStyle(card);
    const chrome =
      parseFloat(style.paddingTop) +
      parseFloat(style.paddingBottom) +
      parseFloat(style.borderTopWidth) +
      parseFloat(style.borderBottomWidth);
    maxHeight = Math.max(maxHeight, contentHeight + chrome);
  });

  const next = maxHeight > 0 ? `${Math.ceil(maxHeight)}px` : '';
  items.forEach(item => {
    if (item.style.minHeight !== next) {
      item.style.minHeight = next;
    }
  });
}

function ChildTopicsReviewList({ topics }: { topics: Topic[] }) {
  const { t } = useTranslation();
  const listRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list) {
      return;
    }

    const syncHeights = () => syncChildTopicCardHeights(list);
    syncHeights();

    if (typeof ResizeObserver === 'undefined') {
      return;
    }

    const observer = new ResizeObserver(syncHeights);
    observer.observe(list);
    return () => observer.disconnect();
  }, [topics]);

  return (
    <div className="topics-page__children-topics-list" ref={listRef}>
      {topics.map(childTopic => (
        <div className="topics-page__children-topic-item" key={childTopic.id}>
          <Link to={`/extranet/${childTopic.id}`} className="topics-page__children-topic">
            <h3>{childTopic.name}</h3>
            <span>{t('extranet.contentCount', { count: childTopic.contentCount ?? 0 })}</span>
          </Link>
        </div>
      ))}
    </div>
  );
}

export default function ExtranetPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { topicID } = useParams();
  const { uuid } = useAuth();
  const { t, i18n } = useTranslation();
  const {
    activeTopicBrand,
    selectedLanguage,
    selectedSortBy,
    publishedTopics,
    publishedTopicsLoading,
    publishedTopicsError,
    expandedTopicKeys,
    activeTopicKey,
    contentItems,
    contentItemsLoading,
    contentItemsAppending,
    contentItemsNextCursor,
    contentItemsError,
    topicDetail
  } = useSelector(state => state.topics);
  const safeTopicTree = useMemo(() => (Array.isArray(publishedTopics) ? publishedTopics : []), [publishedTopics]);
  const safeContentItems = useMemo(() => (Array.isArray(contentItems) ? contentItems : []), [contentItems]);
  const [displayedContentItems, setDisplayedContentItems] = useState({
    loading: true,
    items: safeContentItems,
    error: null
  });
  const [activeTopicTitle, setActiveTopicTitle] = useState(null);
  const [activeTopicPathTitles, setActiveTopicPathTitles] = useState(null);
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const [isSortDropdownOpen, setIsSortDropdownOpen] = useState(false);
  const [isSettingsDropdownOpen, setIsSettingsDropdownOpen] = useState(false);
  const [isTopicSelectModalOpen, setIsTopicSelectModalOpen] = useState(false);
  const [isMobileTopicFilterOpen, setIsMobileTopicFilterOpen] = useState(false);
  const [isMobileConfigureDropdownOpen, setIsMobileConfigureDropdownOpen] = useState(false);
  const [selectedTopicIds, setSelectedTopicIds] = useState([]);
  const [topicSelectModalDraftIds, setTopicSelectModalDraftIds] = useState([]);
  const [favoriteFeedbackDialog, setFavoriteFeedbackDialog] = useState({
    open: false,
    title: '',
    message: ''
  });
  const [favoriteAddSubmitting, setFavoriteAddSubmitting] = useState(false);
  const isFavoriteTopicsOnly = useMemo(() => isFavoriteViewSearch(location.search), [location.search]);
  const prevItemsLoadingRef = useRef(contentItemsLoading);
  const skipNextContentItemsFetchRef = useRef(false);
  const lastContentItemsFetchKeyRef = useRef(null);
  const hadTopicIdInRouteRef = useRef(null);
  const prevRouteTopicIdRef = useRef(topicID);
  const lastFetchedTopicDetailKeyRef = useRef(null);
  const hasMountedSelectedLanguageRef = useRef(false);
  const prevI18nLanguageRef = useRef(i18n.language);
  const filterListRef = useRef(null);
  const mobileFilterListRef = useRef(null);
  const controlsRef = useRef(null);
  const settingsMenuRef = useRef(null);
  const mobileConfigureMenuRef = useRef(null);
  const sidebarRef = useRef(null);
  const contentRef = useRef(null);
  useTopicsSidebarSticky(sidebarRef, contentRef);

  const pageTitle = t('extranet.title');
  const topicTabLabels = {
    all: t('common.allTopics'),
    ate: t('extranet.tabs.ate'),
    vdo: t('extranet.tabs.vdo')
  };
  const languageMenuItems = useMemo(() => getExtranetFilterLanguageItems(t), [t]);
  const sortMenuItems = useMemo(
    () =>
      TOPIC_SORT_OPTIONS.filter(option => !['relevant', 'language', 'created'].includes(option.key)).map(option => ({
        key: option.key,
        value: option.value,
        label: t(option.translationKey)
      })),
    [t]
  );
  const effectiveActiveTopicBrand = activeTopicBrand || 'all';
  const filteredTopics = useMemo(() => {
    if (effectiveActiveTopicBrand === 'all') {
      return safeTopicTree;
    }
    return safeTopicTree.filter(topic => topic.brand === effectiveActiveTopicBrand);
  }, [effectiveActiveTopicBrand, safeTopicTree]);

  const favoriteTopicsFlat = useMemo(() => collectFavoriteTopicsFlat(filteredTopics), [filteredTopics]);
  const contentFilterTopicIdsKey = useMemo(() => {
    const fromModal = selectedTopicIds
      .map(id => (id !== undefined && id !== null ? String(id).trim() : ''))
      .filter(Boolean);
    if (fromModal.length > 0) {
      return fromModal.join(',');
    }
    if (topicID != null && String(topicID).trim() !== '') {
      return String(topicID).trim();
    }
    return '';
  }, [topicID, selectedTopicIds]);
  const contentFilterTopicIds = useMemo(
    () => (contentFilterTopicIdsKey ? contentFilterTopicIdsKey.split(',') : null),
    [contentFilterTopicIdsKey]
  );
  const resetExtranetTopicNav = Boolean(location.state?.resetExtranetTopicNav);

  const hasModalTopicSelection = useMemo(() => selectedTopicIds.length > 0, [selectedTopicIds]);

  const modalSelectedTopicNamesLine = useMemo(() => {
    if (!hasModalTopicSelection) {
      return '';
    }
    const names = selectedTopicIds
      .map(id => {
        const node = findTopicByIdInTree(safeTopicTree, String(id));
        return (node?.displayName || node?.name || '').trim();
      })
      .filter(Boolean);
    return names.join(', ');
  }, [hasModalTopicSelection, selectedTopicIds, safeTopicTree]);

  const topicSelectModalTabs = useMemo(
    () => [
      { id: 'all', label: t('common.allTopics') },
      { id: 'ate', label: t('extranet.tabs.ate') },
      { id: 'vdo', label: t('extranet.tabs.vdo') }
    ],
    [t]
  );

  const selectedTopicIdForFavorite = useMemo(() => {
    if (topicID != null && String(topicID).trim() !== '') {
      return String(topicID).trim();
    }
    if (!activeTopicKey) {
      return null;
    }
    if (activeTopicKey.startsWith('fav-')) {
      const idPart = activeTopicKey.slice(4).trim();
      return idPart || null;
    }
    return String(activeTopicKey).trim() || null;
  }, [topicID, activeTopicKey]);

  const selectedTopicNode = useMemo(
    () => (selectedTopicIdForFavorite ? findTopicByIdInTree(safeTopicTree, selectedTopicIdForFavorite) : null),
    [selectedTopicIdForFavorite, safeTopicTree]
  );
  const isSelectedTopicFavorite = Boolean(selectedTopicNode?.favorite);
  const childTopicsToReview = useMemo(
    () => resolveChildTopicsForReview(topicID, safeTopicTree, topicDetail),
    [topicID, safeTopicTree, topicDetail]
  );

  const displayedTopicTitle = useMemo(() => {
    const labels = {
      all: t('common.allTopics', { lng: selectedLanguage }),
      ate: t('extranet.tabs.ate', { lng: selectedLanguage }),
      vdo: t('extranet.tabs.vdo', { lng: selectedLanguage })
    };
    const activeTab = topicTabs.find(tab => tab.id === effectiveActiveTopicBrand);
    return activeTab ? labels[activeTab.id] || labels.all : labels.all;
  }, [effectiveActiveTopicBrand, selectedLanguage, t]);
  const contentHead = useMemo(
    () =>
      resolveExtranetContentHead({
        topicId: topicID,
        topicTree: safeTopicTree,
        topicDetail,
        fallbackTitle: activeTopicTitle || displayedTopicTitle,
        fallbackPathTitles: activeTopicPathTitles
      }),
    [topicID, safeTopicTree, topicDetail, activeTopicTitle, displayedTopicTitle, activeTopicPathTitles]
  );
  const selectedLanguageLabel = useMemo(() => {
    const selectedItem = languageMenuItems.find(item => item.value === selectedLanguage);
    return selectedItem?.label || t('common.selectLanguage');
  }, [languageMenuItems, selectedLanguage, t]);
  const selectedSortByLabel = useMemo(() => {
    const selectedItem = sortMenuItems.find(item => item.value === selectedSortBy);
    return selectedItem?.label || t('common.sort.latest');
  }, [sortMenuItems, selectedSortBy, t]);
  const hasFilteredTopics = isFavoriteTopicsOnly ? favoriteTopicsFlat.length > 0 : filteredTopics.length > 0;
  const canAddTopicFavorite = Boolean(selectedTopicIdForFavorite && uuid);
  const shouldDimFilterTopics = publishedTopicsLoading && hasFilteredTopics;
  const shouldShowFilterLoading = publishedTopicsLoading && !hasFilteredTopics;
  const shouldShowFilterEmpty = !publishedTopicsLoading && !hasFilteredTopics;

  const navigateExtranet = useCallback(
    ({ topicId, favoriteView }: { topicId?: string | number; favoriteView?: boolean } = {}) => {
      navigate(
        buildExtranetLocation({
          topicId,
          search: location.search,
          favoriteView: favoriteView === undefined ? isFavoriteTopicsOnly : favoriteView
        })
      );
    },
    [navigate, isFavoriteTopicsOnly, location.search]
  );

  const clearModalTopicSelection = useCallback(() => {
    setTopicSelectModalDraftIds(prev => (prev.length === 0 ? prev : []));
    setSelectedTopicIds(prev => {
      if (prev.length === 0) {
        return prev;
      }
      skipNextContentItemsFetchRef.current = true;
      return [];
    });
  }, []);

  const handleFavoriteViewChange = useCallback(
    enabled => {
      navigateExtranet({ topicId: topicID, favoriteView: enabled });
    },
    [navigateExtranet, topicID]
  );

  const handleTabClick = nextBrand => {
    navigateExtranet({ favoriteView: isFavoriteTopicsOnly });
    if (nextBrand === 'all') {
      setActiveTopicTitle(null);
    }

    if (nextBrand === 'all' && effectiveActiveTopicBrand === 'all') {
      dispatch(
        getContentItems({
          activeTopicBrand: 'all',
          selectedLanguage,
          selectedSortBy
        })
      );
    }

    dispatch(setActiveTopicBrand(nextBrand));
  };

  // Sync filter language only when the app UI language actually changes.
  // Do not reset on remount (e.g. Back from content detail) so the user's filter choice is kept.
  useEffect(() => {
    if (prevI18nLanguageRef.current === i18n.language) return;
    prevI18nLanguageRef.current = i18n.language;
    dispatch(setSelectedLanguage(resolveSelectedLanguage()));
  }, [dispatch, i18n.language]);

  useEffect(() => {
    // Defer fetches until after resetExtranetTopicNav applies the default language,
    // otherwise the first paint still uses the previous filter language (e.g. DE).
    if (location.state?.resetExtranetTopicNav) return;
    dispatch(getPublishedTopics(selectedLanguage));
  }, [dispatch, selectedLanguage, location.state]);

  useEffect(() => {
    const hasTopicIdInRoute = topicID != null && String(topicID).trim() !== '';
    const prevHadTopicId = hadTopicIdInRouteRef.current;
    if (!hasTopicIdInRoute && (prevHadTopicId === null || prevHadTopicId === true)) {
      dispatch(clearExpandedTopicKeys());
      dispatch(setActiveTopicKey(''));
      if (prevHadTopicId === true) {
        setActiveTopicTitle(null);
        setActiveTopicPathTitles(null);
      }
    }
    hadTopicIdInRouteRef.current = hasTopicIdInRoute;
  }, [dispatch, topicID]);

  useEffect(() => {
    if (!location.state?.resetExtranetTopicNav) return;
    dispatch(setActiveTopicKey(''));
    dispatch(setSelectedLanguage(resolveSelectedLanguage()));
    dispatch(clearContentItems());
    setActiveTopicTitle(null);
    setActiveTopicPathTitles(null);
    // Clear stale list from the previous filter language while the default-language fetch runs.
    setDisplayedContentItems({ loading: true, items: [], error: null });
    navigate(
      {
        pathname: location.pathname,
        search: location.search,
        hash: location.hash
      },
      { replace: true }
    );
  }, [dispatch, navigate, location.hash, location.pathname, location.search, location.state]);

  useEffect(() => {
    setActiveTopicTitle(null);
    setActiveTopicPathTitles(null);
  }, [effectiveActiveTopicBrand, isFavoriteTopicsOnly, selectedLanguage]);

  useEffect(() => {
    if (!hasMountedSelectedLanguageRef.current) {
      hasMountedSelectedLanguageRef.current = true;
      return;
    }
    lastFetchedTopicDetailKeyRef.current = null;
    dispatch(setTopicDetail(null));
  }, [dispatch, selectedLanguage]);

  useEffect(() => {
    const routeTopicIdChanged = prevRouteTopicIdRef.current !== topicID;
    prevRouteTopicIdRef.current = topicID;

    if (!topicID) {
      lastFetchedTopicDetailKeyRef.current = null;
      if (routeTopicIdChanged) {
        dispatch(setTopicDetail(null));
      }
      return;
    }

    if (routeTopicIdChanged) {
      dispatch(setTopicDetail(null));
    }

    const pathNodes = findTopicPathInTree(filteredTopics, topicID);
    if (pathNodes.length > 0) {
      const leaf = pathNodes[pathNodes.length - 1];
      const title = getTopicDisplayTitle(leaf);
      if (title) {
        setActiveTopicTitle(title);
      }
      if (isFavoriteTopicsOnly) {
        setActiveTopicPathTitles(null);
        dispatch(setActiveTopicKey(`fav-${topicID}`));
      } else {
        setActiveTopicPathTitles(pathNodes.slice(0, -1).map(getTopicDisplayTitle).filter(Boolean));
        dispatch(setExpandedTopicKeys(getExpandedTopicKeysForActivePath(pathNodes)));
        const leafId = tryGetTopicId(leaf);
        dispatch(setActiveTopicKey(leafId !== null ? String(leafId) : ''));
      }
    }

    const topicDetailFetchKey = `${String(topicID)}|${selectedLanguage || ''}`;
    if (lastFetchedTopicDetailKeyRef.current === topicDetailFetchKey) {
      return;
    }
    lastFetchedTopicDetailKeyRef.current = topicDetailFetchKey;
    const requestedKey = topicDetailFetchKey;
    Promise.resolve(dispatch(getActiveTopicDetail({ topicId: topicID, language: selectedLanguage })))
      .then(result => {
        if (result?.status !== 404) return;
        if (lastFetchedTopicDetailKeyRef.current !== requestedKey) return;
        dispatch(setActiveTopicBrand('all'));
        setActiveTopicTitle(null);
        setActiveTopicPathTitles(null);
        clearModalTopicSelection();
        navigateExtranet({ favoriteView: false });
      })
      .catch(() => {});
  }, [clearModalTopicSelection, dispatch, filteredTopics, isFavoriteTopicsOnly, navigateExtranet, selectedLanguage, topicID]);

  useEffect(() => {
    if (publishedTopicsLoading || !activeTopicKey) return;
    const filterListEl = isMobileTopicFilterOpen ? mobileFilterListRef.current : filterListRef.current;
    if (!filterListEl) return;
    const activeTopicButton = filterListEl.querySelector('.topics-page__filter-button--active');
    if (!activeTopicButton) return;
    activeTopicButton.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [publishedTopicsLoading, activeTopicKey, topicID, filteredTopics, isFavoriteTopicsOnly, isMobileTopicFilterOpen]);

  useEffect(() => {
    return () => {
      dispatch(setPublishedTopicsLoading());
    };
  }, [dispatch]);

  useEffect(() => {
    // Same as published topics: wait for resetExtranetTopicNav to restore default language first.
    if (resetExtranetTopicNav) {
      lastContentItemsFetchKeyRef.current = null;
      return;
    }
    const fetchKey = `${effectiveActiveTopicBrand || 'all'}|${selectedLanguage || ''}|${selectedSortBy || ''}|${contentFilterTopicIdsKey}`;
    if (skipNextContentItemsFetchRef.current) {
      skipNextContentItemsFetchRef.current = false;
      lastContentItemsFetchKeyRef.current = fetchKey;
      return;
    }
    if (lastContentItemsFetchKeyRef.current === fetchKey) {
      return;
    }
    lastContentItemsFetchKeyRef.current = fetchKey;
    dispatch(
      getContentItems({
        activeTopicBrand: effectiveActiveTopicBrand,
        selectedLanguage,
        selectedSortBy,
        ...(contentFilterTopicIdsKey ? { topicIds: contentFilterTopicIdsKey.split(',') } : {})
      })
    );
  }, [
    dispatch,
    effectiveActiveTopicBrand,
    selectedLanguage,
    selectedSortBy,
    contentFilterTopicIdsKey,
    resetExtranetTopicNav
  ]);

  useEffect(() => {
    const handleOutsideClick = event => {
      if (controlsRef.current?.contains(event.target)) return;
      if (settingsMenuRef.current?.contains(event.target)) return;
      if (mobileConfigureMenuRef.current?.contains(event.target)) return;
      setIsLanguageDropdownOpen(false);
      setIsSortDropdownOpen(false);
      setIsSettingsDropdownOpen(false);
      setIsMobileConfigureDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);

  useEffect(() => {
    const wasLoading = prevItemsLoadingRef.current;
    prevItemsLoadingRef.current = contentItemsLoading;

    if (wasLoading && !contentItemsLoading) {
      setDisplayedContentItems({ loading: false, items: safeContentItems, error: contentItemsError });
    } else if (contentItemsLoading && !wasLoading && !contentItemsAppending) {
      setDisplayedContentItems({ loading: true, items: safeContentItems, error: null });
    } else if (!contentItemsLoading) {
      setDisplayedContentItems({ loading: false, items: safeContentItems, error: contentItemsError });
    }
  }, [contentItemsLoading, contentItemsAppending, safeContentItems, contentItemsError]);

  const handleLoadMore = () => {
    if (!contentItemsNextCursor || contentItemsLoading) {
      return;
    }

    dispatch(
      getContentItems({
        activeTopicBrand: effectiveActiveTopicBrand,
        selectedLanguage,
        selectedSortBy,
        cursor: contentItemsNextCursor,
        append: true,
        ...(contentFilterTopicIds ? { topicIds: contentFilterTopicIds } : {})
      })
    );
  };

  const closeControlDropdowns = () => {
    setIsLanguageDropdownOpen(false);
    setIsSortDropdownOpen(false);
    setIsSettingsDropdownOpen(false);
  };

  const handleOpenContentDetail = itemId => {
    navigate(`/extranet/content/${itemId}/${selectedLanguage}`);
    // TODO: need to adjust the drawer page first, then re-enable this again
    // closeControlDropdowns();
    // const currentLocationState =
    //   location?.state && typeof location.state === 'object' ? { ...location.state } : undefined;
    // if (currentLocationState) {
    //   delete currentLocationState.backgroundLocation;
    //   delete currentLocationState.openAsDrawer;
    // }
    // requestAnimationFrame(() => {
    //   navigate({
    //     pathname: `/extranet/content/${itemId}/${selectedLanguage}`,
    //     state: {
    //       backgroundLocation: {
    //         pathname: location.pathname,
    //         search: location.search,
    //         hash: location.hash,
    //         state: currentLocationState
    //       },
    //       openAsDrawer: true
    //     }
    //   });
    // });
  };

  const handleTopicSelectModalDraftToggle = topicId => {
    if (topicId === undefined || topicId === null) {
      return;
    }
    const key = String(topicId);
    setTopicSelectModalDraftIds(prev => {
      const has = prev.some(id => String(id) === key);
      if (has) {
        return prev.filter(id => String(id) !== key);
      }
      return [...prev, topicId];
    });
  };

  const handleOpenTopicSelectModal = useCallback(() => {
    handleFavoriteViewChange(false);
    setIsSettingsDropdownOpen(false);
    setIsMobileConfigureDropdownOpen(false);
    setIsMobileTopicFilterOpen(false);
    setTopicSelectModalDraftIds([...selectedTopicIds]);
    setIsTopicSelectModalOpen(true);
  }, [handleFavoriteViewChange, selectedTopicIds]);

  const handleTopicSelectModalClose = () => {
    const committed = [...topicSelectModalDraftIds];
    setSelectedTopicIds(committed);
    setIsTopicSelectModalOpen(false);
    if (committed.length > 0) {
      dispatch(setActiveTopicKey(''));
      setActiveTopicTitle(null);
      setActiveTopicPathTitles(null);
      navigate(
        buildExtranetLocation({
          search: location.search,
          favoriteView: isFavoriteTopicsOnly
        })
      );
      const topicIds = committed.map(id => (id !== undefined && id !== null ? String(id).trim() : '')).filter(Boolean);
      skipNextContentItemsFetchRef.current = true;
      dispatch(
        getContentItems({
          activeTopicBrand: effectiveActiveTopicBrand,
          selectedLanguage,
          selectedSortBy,
          topicIds
        })
      );
    }
  };

  const handleFavoriteMenuAction = async () => {
    if (!canAddTopicFavorite || favoriteAddSubmitting) {
      return;
    }
    setFavoriteAddSubmitting(true);
    try {
      if (isSelectedTopicFavorite) {
        await dispatch(removeUserTopicFavorite({ uuid, topicId: selectedTopicIdForFavorite }));
        dispatch(setActiveTopicKey(''));
        setActiveTopicTitle(null);
        setActiveTopicPathTitles(null);
        navigate(
          buildExtranetLocation({
            search: location.search,
            favoriteView: isFavoriteTopicsOnly
          })
        );
        setFavoriteFeedbackDialog({
          open: true,
          title: t('extranet.favoriteDialog.title'),
          message: t('extranet.favoriteDialog.removeSuccess')
        });
      } else {
        await dispatch(addUserTopicFavorite({ uuid, topicId: selectedTopicIdForFavorite }));
        setFavoriteFeedbackDialog({
          open: true,
          title: t('extranet.favoriteDialog.title'),
          message: t('extranet.favoriteDialog.addSuccess')
        });
      }
      setIsSettingsDropdownOpen(false);
      setIsMobileConfigureDropdownOpen(false);
    } catch (err) {
      window.alert(
        err?.message ||
          (isSelectedTopicFavorite ? t('extranet.favoriteDialog.removeError') : t('extranet.favoriteDialog.addError'))
      );
    } finally {
      setFavoriteAddSubmitting(false);
    }
  };

  const renderSettingsMenuList = () => (
    <ul className="topics-page__settings-menu-list" role="menu">
      <li role="none">
        <button
          className="topics-page__settings-menu-item"
          type="button"
          role="menuitem"
          disabled={!canAddTopicFavorite || favoriteAddSubmitting}
          onClick={() => {
            handleFavoriteMenuAction();
          }}>
          {favoriteAddSubmitting
            ? isSelectedTopicFavorite
              ? t('extranet.favoriteAction.removing')
              : t('extranet.favoriteAction.adding')
            : isSelectedTopicFavorite
              ? t('extranet.favoriteAction.remove')
              : t('extranet.favoriteAction.add')}
        </button>
      </li>
      <li role="none">
        <button
          className="topics-page__settings-menu-item"
          type="button"
          role="menuitem"
          onClick={handleOpenTopicSelectModal}>
          {t('extranet.showAllTopics')}
        </button>
      </li>
    </ul>
  );

  const renderTopicNode = (topic, path, level = 1) => {
    if (level > 3) return null;

    const nodeKey = getTopicTreeNodeKey(topic, path);
    const topicName = topic?.displayName || topic?.name || `${t('common.topic')} ${nodeKey}`;
    const canExpand = level < 3 && hasChildren(topic);
    const isExpanded = !!expandedTopicKeys[nodeKey];
    const isActive = isActiveExtranetTopic(topic, topicID);

    const handleTopicClick = () => {
      setIsMobileTopicFilterOpen(false);
      clearModalTopicSelection();
      dispatch(setActiveTopicKey(nodeKey));
      setActiveTopicTitle(topicName);
      const fullPathTitles = getTopicPathTitles(filteredTopics, path);
      const parentTitles = fullPathTitles.length > 1 ? fullPathTitles.slice(0, -1) : [];
      const filteredParentTitles = parentTitles.filter(title => title && title !== topicName);
      setActiveTopicPathTitles(filteredParentTitles);
      const topicId = tryGetTopicId(topic);
      if (topicId !== null && topicId !== undefined && topicId !== '') {
        navigateExtranet({ topicId, favoriteView: false });
      }
    };

    return (
      <li className={`topics-page__filter-item topics-page__filter-item--level-${level}`} key={nodeKey}>
        <button
          className={`topics-page__filter-button topics-page__filter-button--level-${level} ${
            isActive ? 'topics-page__filter-button--active' : ''
          } ${isExpanded ? 'topics-page__filter-button--expanded' : ''}`}
          type="button"
          onClick={handleTopicClick}>
          <span>{topicName}</span>
          {canExpand ? (
            <span
              className={`topics-page__chevron ${isExpanded ? 'topics-page__chevron--expanded' : ''}`}
              aria-hidden
              onClick={event => {
                event.preventDefault();
                event.stopPropagation();
                dispatch(toggleExpandedTopicKey(nodeKey));
              }}>
              ˅
            </span>
          ) : null}
        </button>

        {canExpand && isExpanded ? (
          <ul className={`topics-page__filter-sublist topics-page__filter-sublist--level-${level + 1}`}>
            {topic.children.map((childTopic, index) => renderTopicNode(childTopic, [...path, index], level + 1))}
          </ul>
        ) : null}
      </li>
    );
  };

  const handleFavoriteTopicClick = useCallback(
    (topic, index) => {
      setIsMobileTopicFilterOpen(false);
      const nodeKey =
        topic?.id !== undefined && topic?.id !== null ? `fav-${topic.id}` : `fav-${index}-${topic?.name || ''}`;
      const topicName = topic?.displayName || topic?.name || `${t('common.topic')} ${index}`;
      clearModalTopicSelection();
      dispatch(setActiveTopicKey(nodeKey));
      setActiveTopicTitle(topicName);
      setActiveTopicPathTitles(null);
      const topicId = tryGetTopicId(topic);
      if (topicId !== null && topicId !== undefined && topicId !== '') {
        navigateExtranet({ topicId, favoriteView: true });
      }
    },
    [clearModalTopicSelection, dispatch, navigateExtranet, t]
  );

  const renderTopicFilterList = listRef => (
    <>
      <div className="topics-page__tabs">
        {topicTabs.map(tab => (
          <button
            className={`topics-page__tab ${effectiveActiveTopicBrand === tab.id ? 'topics-page__tab--active' : ''}`}
            type="button"
            aria-pressed={effectiveActiveTopicBrand === tab.id}
            onClick={() => handleTabClick(tab.id)}
            key={tab.id}>
            {topicTabLabels[tab.id] || topicTabLabels.all}
          </button>
        ))}
      </div>
      <ul
        ref={listRef}
        className={`topics-page__filter-list${shouldDimFilterTopics ? ' topics-page__filter-list--loading' : ''}`}
        aria-busy={shouldDimFilterTopics}>
        {shouldShowFilterLoading ? <li className="topics-page__filter-empty">{t('extranet.loadingTopics')}</li> : null}
        {shouldShowFilterEmpty ? <li className="topics-page__filter-empty">{t('extranet.noTopicsFound')}</li> : null}
        {hasFilteredTopics
          ? isFavoriteTopicsOnly
            ? favoriteTopicsFlat.map((topic, index) => {
                const nodeKey =
                  topic?.id !== undefined && topic?.id !== null
                    ? `fav-${topic.id}`
                    : `fav-${index}-${topic?.name || ''}`;
                const topicName = topic?.displayName || topic?.name || `${t('common.topic')} ${index}`;
                const isActive = isActiveExtranetTopic(topic, topicID);
                return (
                  <li
                    className="topics-page__filter-item topics-page__filter-item--level-1 topics-page__filter-item--favorite-flat"
                    key={nodeKey}>
                    <button
                      className={`topics-page__filter-button topics-page__filter-button--level-1 ${
                        isActive ? 'topics-page__filter-button--active' : ''
                      }`}
                      type="button"
                      onClick={() => handleFavoriteTopicClick(topic, index)}>
                      <span>{topicName}</span>
                    </button>
                  </li>
                );
              })
            : filteredTopics.map((topic, index) => renderTopicNode(topic, [index]))
          : null}
      </ul>
    </>
  );

  return (
    <Page className={`topics-page`} title={pageTitle}>
      <Breadcrumb data={[{ title: t('extranet.breadcrumb') }]} />
      <Grid className="topics-page__container container">
        <h1 className="topics-page__title">{pageTitle}</h1>
        <Grid className="topics-page__layout">
          <aside ref={sidebarRef} className="topics-page__sidebar">
            <div className="topics-page__sidebar-content">{renderTopicFilterList(filterListRef)}</div>

            <div className="topics-page__sidebar-footer">
              <Toggle
                checked={isFavoriteTopicsOnly}
                label={t('extranet.favoritesToggle')}
                onChange={() => handleFavoriteViewChange(!isFavoriteTopicsOnly)}
              />
              <CustomDropDown
                ref={settingsMenuRef}
                className="topics-page__settings-menu"
                open={isSettingsDropdownOpen}
                onOpenChange={setIsSettingsDropdownOpen}
                triggerOpensOnClick={false}
                closeOnPanelClick={false}
                placement="topRight"
                trigger={
                  <button
                    className="topics-page__settings"
                    type="button"
                    aria-label={t('common.settings')}
                    aria-haspopup="menu"
                    onClick={() => {
                      setIsSettingsDropdownOpen(prev => !prev);
                      setIsLanguageDropdownOpen(false);
                      setIsSortDropdownOpen(false);
                    }}>
                    <SettingIcon width={26} height={26} />
                  </button>
                }>
                {renderSettingsMenuList()}
              </CustomDropDown>
            </div>
          </aside>

          <section ref={contentRef} className="topics-page__content">
            <div className="topics-page__content-head">
              <div className="topics-page__content-head-row">
                <div className="topics-page__content-head-title">
                  {hasModalTopicSelection ? (
                    <>
                      <p className="topics-page__content-head-kicker">{t('extranet.selectedTopicsKicker')}</p>
                      <h3
                        className="topics-page__content-head-selected-names"
                        aria-label={modalSelectedTopicNamesLine || t('extranet.selectedTopicsKicker')}>
                        {modalSelectedTopicNamesLine || '—'}
                      </h3>
                    </>
                  ) : (
                    <>
                      {Array.isArray(contentHead.pathTitles) && contentHead.pathTitles.length > 0 ? (
                        <div className="topics-page__content-head-path" aria-label={t('extranet.topicPathAria')}>
                          <span className="topics-page__content-head-path-parent">
                            {contentHead.pathTitles.join(' / ')}
                          </span>
                        </div>
                      ) : null}
                      <h2>{contentHead.title}</h2>
                    </>
                  )}
                </div>
                <div className="topics-page__content-head-filter-mobile">
                  <button type="button" onClick={() => setIsMobileTopicFilterOpen(true)}>
                    {t('extranet.filterByTopics')}
                  </button>
                </div>
              </div>
              <div className="topics-page__controls" ref={controlsRef}>
                <div className="topics-page__control-menu">
                  <CustomDropDown
                    placement="bottomLeft"
                    value={selectedLanguage}
                    defaultValue={getDefaultSearchLanguage()}
                    onReset={next => dispatch(setSelectedLanguage(next))}
                    resetAriaLabel={t('common.button.resetToDefault')}
                    open={isLanguageDropdownOpen}
                    onOpenChange={open => {
                      setIsLanguageDropdownOpen(open);
                      if (open) {
                        setIsSortDropdownOpen(false);
                        setIsSettingsDropdownOpen(false);
                      }
                    }}
                    trigger={
                      <button
                        className="topics-page__controls-btn topics-page__control-language-btn"
                        type="button"
                        aria-expanded={isLanguageDropdownOpen}>
                        <span>{selectedLanguageLabel}</span>
                      </button>
                    }>
                    <ul className="dropdown-selection">
                      {languageMenuItems.map(item => (
                        <li key={item.key}>
                          <button
                            className={`topics-page__control-menu-item${
                              item.value === selectedLanguage ? ' topics-page__control-menu-item--active' : ''
                            }`}
                            type="button"
                            onClick={() => {
                              dispatch(setSelectedLanguage(item.value || ''));
                              setIsLanguageDropdownOpen(false);
                            }}>
                            {item.label}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </CustomDropDown>
                </div>

                <div className="topics-page__control-menu">
                  <SortSelectDropdown
                    items={sortMenuItems}
                    label={selectedSortByLabel}
                    defaultValue={null}
                    onReset={() => dispatch(setSelectedSortBy(null))}
                    resetAriaLabel={t('common.button.resetToDefault')}
                    placement="bottomRight"
                    triggerClassName="topics-page__controls-btn topics-page__control-sort-btn"
                    triggerLabelClassName=""
                    triggerIcon={null}
                    open={isSortDropdownOpen}
                    onOpenChange={open => {
                      setIsSortDropdownOpen(open);
                      if (open) {
                        setIsLanguageDropdownOpen(false);
                        setIsSettingsDropdownOpen(false);
                      }
                    }}
                    menuClassName="topics-page__control-menu-list"
                    menuItemClassName=""
                    itemClassName="topics-page__control-menu-item"
                    activeItemClassName="topics-page__control-menu-item--active"
                    selectedKey={selectedSortBy}
                    getItemValue={item => item.value}
                    onSelect={item => {
                      dispatch(setSelectedSortBy(item.value || 'relevant'));
                      setIsSortDropdownOpen(false);
                    }}
                  />
                </div>
              </div>
            </div>

            <div
              className={`topics-page__items${
                !displayedContentItems.error && displayedContentItems.loading && displayedContentItems.items?.length > 0
                  ? ' topics-page__items--dimmed'
                  : ''
              }`}>
              {displayedContentItems.error ? (
                <p className="topics-page__filter-empty">{t('extranet.loadItemsError')}</p>
              ) : displayedContentItems.loading && !(displayedContentItems.items?.length > 0) ? (
                <ContentListSkeleton count={3} />
              ) : displayedContentItems.items?.length > 0 ? (
                <div>
                  {displayedContentItems.items.map(item => (
                    <ContentItem item={item} key={item.id} onClick={() => handleOpenContentDetail(item.id)} />
                  ))}
                  {contentItemsNextCursor ? (
                    <Button
                      text
                      onClick={handleLoadMore}
                      disabled={contentItemsLoading}
                      additionalClassName="topics-page__load-more">
                      {contentItemsLoading ? t('common.loading') : t('common.button.loadMore')}
                    </Button>
                  ) : null}
                </div>
              ) : childTopicsToReview.length > 0 ? (
                <div className="topics-page__children-topics">
                  <p>{t('extranet.checkChildCategories')}</p>
                  <ChildTopicsReviewList topics={childTopicsToReview} />
                </div>
              ) : (
                <p className="topics-page__filter-empty">{t('extranet.noTopicItemsFound')}</p>
              )}
            </div>
          </section>
        </Grid>
      </Grid>

      <Dialog
        open={isMobileTopicFilterOpen}
        onClose={() => setIsMobileTopicFilterOpen(false)}
        fullWidth
        maxWidth="xs"
        className="topics-page__mobile-filter-dialog"
        PaperProps={{ className: 'topics-page__mobile-filter-dialog-paper' }}>
        <DialogTitle component="div" className="topics-page__mobile-filter-dialog-title">
          <span>{t('extranet.filterByTopics')}</span>
          <IconButton
            className="topics-page__mobile-filter-dialog-close"
            aria-label={t('close')}
            onClick={() => setIsMobileTopicFilterOpen(false)}>
            <CrossIcon width={16} height={16} />
          </IconButton>
        </DialogTitle>
        <DialogContent className="topics-page__mobile-filter-dialog-content">
          <div className="topics-page__mobile-filter-panel">
            <div className="topics-page__sidebar-content topics-page__sidebar-content--mobile-dialog">
              {renderTopicFilterList(mobileFilterListRef)}
            </div>
            <div className="topics-page__mobile-filter-footer">
              <Toggle
                checked={isFavoriteTopicsOnly}
                label={t('extranet.favoritesToggle')}
                onChange={() => handleFavoriteViewChange(!isFavoriteTopicsOnly)}
              />
              <CustomDropDown
                ref={mobileConfigureMenuRef}
                className="topics-page__settings-menu topics-page__mobile-filter-configure-menu"
                open={isMobileConfigureDropdownOpen}
                onOpenChange={setIsMobileConfigureDropdownOpen}
                triggerOpensOnClick={false}
                closeOnPanelClick={false}
                placement="top"
                trigger={
                  <button
                    className="topics-page__mobile-filter-configure"
                    type="button"
                    aria-haspopup="menu"
                    onClick={() => {
                      setIsMobileConfigureDropdownOpen(prev => !prev);
                      setIsLanguageDropdownOpen(false);
                      setIsSortDropdownOpen(false);
                      setIsSettingsDropdownOpen(false);
                    }}>
                    {t('extranet.configurePreferredTopics')}
                  </button>
                }>
                {renderSettingsMenuList()}
              </CustomDropDown>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <TopicSelectModal
        open={isTopicSelectModalOpen}
        onClose={handleTopicSelectModalClose}
        topicTree={safeTopicTree}
        selectedTopicIds={topicSelectModalDraftIds}
        onToggleTopic={handleTopicSelectModalDraftToggle}
        loading={publishedTopicsLoading}
        error={publishedTopicsError ? String(publishedTopicsError) : null}
        title={t('extranet.topicSelectModalTitle')}
        confirmButtonLabel={t('common.button.ok')}
        loadingMessage={t('extranet.loadingTopics')}
        tabs={topicSelectModalTabs}
      />

      <Dialog
        open={favoriteFeedbackDialog.open}
        onClose={() => setFavoriteFeedbackDialog({ open: false, title: '', message: '' })}
        className="result-popup">
        <DialogContent className="result-popup-title">{favoriteFeedbackDialog.message}</DialogContent>
        <DialogActions>
          <Button
            onClick={() => setFavoriteFeedbackDialog({ open: false, title: '', message: '' })}
            additionalClassName="topics-page__dialog-ok">
            {t('common.button.ok')}
          </Button>
        </DialogActions>
      </Dialog>
    </Page>
  );
}
